import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from './constants';
import SearchSupport from './components/supports/SearchSupport';
import SupportDisplay from './components/supports/SupportDisplay';
import CharactersSupportHeader from './components/supports/CharactersSupportHeader';
import LoadingScreen from './components/shared/LoadingScreen';
import SEO from './components/shared/SEO';

const CharactersSupport = () => {
    const { t } = useTranslation();
    const [supports, setSupports] = useState([]);
    const [filteredSupports, setFilteredSupports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState('All');
    const [selectedRarity, setSelectedRarity] = useState('All');

    const [displayLimit, setDisplayLimit] = useState(36); // Initial batch size

    const fetchSupports = useCallback(async () => {
        try {
            setLoading(true);
            const headers = {};
            // Interceptor handles Authorization
            const response = await fetch(`${API_BASE_URL}/api/supports`, { headers });
            if (!response.ok) {
                throw new Error(t('CharactersSupport.error_fetch'));
            }
            const data = await response.json();
            setSupports(data);
            setFilteredSupports(data);
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchSupports();
    }, [fetchSupports]);

    useEffect(() => {
        let results = supports;

        // Filter by Type
        if (selectedType !== 'All') {
            results = results.filter(s => s.Support_Type === selectedType);
        }

        // Filter by Rarity
        if (selectedRarity !== 'All') {
            results = results.filter(s => s.rarity === selectedRarity);
        }

        // Filter by Name (Char Name or Card Name)
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            results = results.filter(s =>
                (s.char_name && s.char_name.toLowerCase().includes(term)) ||
                (s.card_name && s.card_name.toLowerCase().includes(term))
            );
        }

        setFilteredSupports(results);
        setDisplayLimit(36); // Reset limit on filter change
    }, [searchTerm, selectedType, selectedRarity, supports]);

    // Independent Infinite Scroll for Window
    useEffect(() => {
        const handleScroll = () => {
            if (
                window.innerHeight + window.scrollY >= document.body.offsetHeight - 500 &&
                displayLimit < filteredSupports.length
            ) {
                setDisplayLimit(prev => Math.min(prev + 36, filteredSupports.length));
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [filteredSupports.length, displayLimit]);

    if (loading) return <LoadingScreen />;
    if (error) return (
        <div className="min-h-screen pt-24 text-center text-red-600">
            Error: {error}
        </div>
    );

    const visibleSupports = filteredSupports.slice(0, displayLimit);

    return (
        <div className="min-h-screen bg-panel-gray pt-20 pb-24">
            <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8">
                <SEO title={t('MetaCharactersSupport.title')} description={t('MetaCharactersSupport.description')} />
                {/* Header */}
                <CharactersSupportHeader count={filteredSupports.length} />

                {/* Filters */}
                <SearchSupport
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    selectedType={selectedType}
                    onTypeChange={setSelectedType}
                    selectedRarity={selectedRarity}
                    onRarityChange={setSelectedRarity}
                />

                {/* Grid */}
                {visibleSupports.length > 0 ? (
                    <div className="grid grid-cols-2 min-[450px]:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6 justify-items-center">
                        {visibleSupports.map(support => (
                            <SupportDisplay key={support.Id} support={support} />
                        ))}
                        {/* Loading More Indicator */}
                        {displayLimit < filteredSupports.length && (
                            <div className="col-span-full py-8 text-center">
                                <svg className="animate-spin h-8 w-8 text-blue-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-20 text-gray-500">
                        <p className="text-xl">{t('CharactersSupport.empty_msg')}</p>
                        <button
                            onClick={() => { setSearchTerm(''); setSelectedType('All'); }}
                            className="mt-4 text-blue-500 hover:underline"
                        >
                            {t('CharactersSupport.button_clear')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CharactersSupport;
// Revisado: 2025-12-18 [I18n Scan]
